import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useQueryClient,
} from "@tanstack/react-query";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { IPagePayload } from "@src/types/api/pages";
import { TResponse } from "../../../types/common";
import { detailPage, savePage } from "./callbacks";

export const usePageSave = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<IPagePayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IPagePayload>,
    unknown,
    IPagePayload
  > = {}
): UseMutationResult<TResponse<IPagePayload>, unknown, IPagePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IPagePayload>, unknown, IPagePayload>({
    mutationFn: async (data: IPagePayload) => {
      const response = await savePage(data);
      return response.data;
    },
    onSuccess: (res, ...rest) => {
      if (res.data?.pageType) {
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.PAGE_DETAIL, res.data.pageType],
        });
      }

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

/* ---------------------- Get Page Detail Query ---------------------- */
export const usePageDetail = (
  type: string,
  options?: Omit<
    UseQueryOptions<TResponse<IPagePayload>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<IPagePayload>, Error> => {
  return useQuery<TResponse<IPagePayload>, Error>({
    queryKey: [API_ENDPOINTS.PAGE_DETAIL, type],
    queryFn: async () => {
      const res = await detailPage(type);
      return res.data;
    },
    enabled: !!type,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};
